import { useCallback, useEffect, useState } from 'react';
import { familyNameService, userService } from '../services/api';

const familyNamesCache = {
  data: null as any[] | null,
  promise: null as Promise<any> | null,
  error: '',
};

const usersCache = {
  data: null as any[] | null,
  promise: null as Promise<any> | null,
  error: '',
};

const userDetailCache = new Map<number, { data: any; promise: Promise<any> | null; error: string }>();

export const useFamilyNames = () => {
  const [data, setData] = useState<any[]>(familyNamesCache.data ?? []);
  const [loading, setLoading] = useState<boolean>(Boolean(familyNamesCache.promise));
  const [error, setError] = useState<string>(familyNamesCache.error);

  const refresh = useCallback(async () => {
    if (familyNamesCache.promise) {
      try {
        setLoading(true);
        const response = await familyNamesCache.promise;
        familyNamesCache.data = response?.data ?? response ?? [];
        setData(familyNamesCache.data);
        setError('');
      } catch (err: any) {
        familyNamesCache.error = err?.response?.data?.message || 'Failed to load family names';
        setError(familyNamesCache.error);
      } finally {
        setLoading(false);
      }
      return;
    }

    setLoading(true);
    familyNamesCache.error = '';
    familyNamesCache.promise = familyNameService.getAll().then((response) => {
      const resolved = response?.data ?? response ?? [];
      familyNamesCache.data = resolved;
      return resolved;
    });

    try {
      const resolved = await familyNamesCache.promise;
      setData(resolved);
      setError('');
    } catch (err: any) {
      familyNamesCache.error = err?.response?.data?.message || 'Failed to load family names';
      setError(familyNamesCache.error);
    } finally {
      setLoading(false);
      familyNamesCache.promise = null;
    }
  }, []);

  useEffect(() => {
    if (familyNamesCache.data && familyNamesCache.data.length >= 0) {
      setData(familyNamesCache.data);
      setLoading(false);
      return;
    }

    void refresh();
  }, [refresh]);

  return { data, loading, error, refresh };
};

export const useUsers = () => {
  const [data, setData] = useState<any[]>(usersCache.data ?? []);
  const [loading, setLoading] = useState<boolean>(Boolean(usersCache.promise));
  const [error, setError] = useState<string>(usersCache.error);

  const refresh = useCallback(async () => {
    if (usersCache.promise) {
      try {
        setLoading(true);
        const response = await usersCache.promise;
        usersCache.data = response?.data ?? response ?? [];
        setData(usersCache.data);
        setError('');
      } catch (err: any) {
        usersCache.error = err?.response?.data?.message || 'Failed to load users';
        setError(usersCache.error);
      } finally {
        setLoading(false);
      }
      return;
    }

    setLoading(true);
    usersCache.error = '';
    usersCache.promise = userService.getAllUsers().then((response) => {
      const resolved = response?.data ?? response ?? [];
      usersCache.data = resolved;
      return resolved;
    });

    try {
      const resolved = await usersCache.promise;
      setData(resolved);
      setError('');
    } catch (err: any) {
      usersCache.error = err?.response?.data?.message || 'Failed to load users';
      setError(usersCache.error);
    } finally {
      setLoading(false);
      usersCache.promise = null;
    }
  }, []);

  useEffect(() => {
    if (usersCache.data && usersCache.data.length >= 0) {
      setData(usersCache.data);
      setLoading(false);
      return;
    }

    void refresh();
  }, [refresh]);

  return { data, loading, error, refresh };
};

export const useUserDetail = (userId?: number) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  const refresh = useCallback(async () => {
    if (!userId) {
      setData(null);
      setLoading(false);
      return;
    }

    let cache = userDetailCache.get(userId);
    if (!cache) {
      cache = { data: null, promise: null, error: '' };
      userDetailCache.set(userId, cache);
    }

    if (cache.promise) {
      try {
        setLoading(true);
        const response = await cache.promise;
        cache.data = response?.data ?? response ?? null;
        setData(cache.data);
        setError('');
      } catch (err: any) {
        cache.error = err?.response?.data?.message || 'Failed to load user details';
        setError(cache.error);
      } finally {
        setLoading(false);
      }
      return;
    }

    setLoading(true);
    cache.error = '';
    cache.promise = userService.getUserById(userId).then((response) => {
      const resolved = response?.data ?? response ?? null;
      cache.data = resolved;
      return resolved;
    });

    try {
      const resolved = await cache.promise;
      setData(resolved);
      setError('');
    } catch (err: any) {
      cache.error = err?.response?.data?.message || 'Failed to load user details';
      setError(cache.error);
    } finally {
      setLoading(false);
      cache.promise = null;
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) {
      setData(null);
      setLoading(false);
      return;
    }

    void refresh();
  }, [userId, refresh]);

  return { data, loading, error, refresh };
};
